import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Container, Row, Col } from 'react-bootstrap'
import moment from 'moment-timezone'
import {
  Button,
  useErrorService,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import API from '../../utils/API'
import { getTimezoneOffsetMinutes } from '../../utils/timezone'
import { useSessionTimezone } from '../../hooks/useSessionTimezone'
import { convertDateAndSlotToUTC } from '../../utils/sessionDateUtils'
import CoachDetailsSection from './CoachDetailsSection'
import SessionDatePicker from './SessionDatePicker'
import TimeSlotPicker from './TimeSlotPicker'
import classnames from 'classnames'
import TagManager from 'react-gtm-module'
import {
  SAME_DAY_CONFLICT_CODE,
  SAME_DAY_DUPLICATE_IN_REQUEST_CODE,
  MAX_SESSIONS_PER_WEEK_EXCEEDED_CODE,
  WEEKLY_LIMIT_WARNING_CODE,
} from '@abroad/rest-client'
import ExperienceEvent from '../../constants/events'
import useSchedulingRestrictions from '../../hooks/useSchedulingRestrictions'
import WeeklySessionWarningModal from './WeeklySessionWarningModal'
import {
  gtmScheduleMultipleSessionsAction,
  gtmScheduleSessionAction,
} from '../quests/gtmfunctions'

const isSessionErrorClickable = (code) =>
  code === SAME_DAY_CONFLICT_CODE ||
  code === SAME_DAY_DUPLICATE_IN_REQUEST_CODE ||
  code === MAX_SESSIONS_PER_WEEK_EXCEEDED_CODE

const isSessionRestrictionError = (code) =>
  code === SAME_DAY_CONFLICT_CODE ||
  code === SAME_DAY_DUPLICATE_IN_REQUEST_CODE ||
  code === MAX_SESSIONS_PER_WEEK_EXCEEDED_CODE ||
  code === WEEKLY_LIMIT_WARNING_CODE

/**
 * Quest Session Scheduling Calendar Component
 * Handles scheduling for quest sessions with sessionIndex-based labels
 * Shows: Pre-Session (sessionIndex: 1), Post-Session (sessionIndex: 2), Session 3 (sessionIndex: 3), etc.
 */
const QuestSessionSchedulingCalendar = ({
  questId = null,
  onScheduleSuccess = null,
  coachDetails: coachDetailsProp = null,
}) => {
  const history = useHistory()
  const { questId: questIdFromParams } = useParams()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const { user } = useUserService()

  const userId = user?._id
  const finalQuestId = questId || questIdFromParams
  const applySessionErrorsRef = useRef(() => {})

  const {
    weeklyModal,
    isConfirmingWeekly,
    closeWeeklyModal,
    handleWeeklyConfirm,
    runScheduling,
  } = useSchedulingRestrictions({
    onSuccess: (data) => {
      setSessionSlotErrors({})
      Notification.showNotification(
        data?.message || 'Your sessions have been scheduled successfully!',
        'success',
      )
      if (onScheduleSuccess) {
        onScheduleSuccess(data?.scheduledSessions)
      } else {
        history.push(`/your-experiences/${finalQuestId}/session-list`)
      }
    },
    onSameDayConflict: (message) => {
      Notification.showNotification(message, 'danger')
    },
    onSessionErrors: (errors) => {
      applySessionErrorsRef.current(errors)
    },
    onError: (message) => {
      Notification.showNotification(message, 'danger')
    },
  })

  // Use shared timezone hook
  const { clientTimezone, setClientTimezone, handleTimezoneChange } =
    useSessionTimezone()

  // State for current session index
  const [currentSessionIndex, setCurrentSessionIndex] = useState(null)
  // Store selected slots for each session (keyed by sessionIndex)
  const [sessionSlots, setSessionSlots] = useState({})
  const [sessionSlotErrors, setSessionSlotErrors] = useState({})
  const [coachDetails, setCoachDetails] = useState(coachDetailsProp)
  const [isLoadingCoach, setIsLoadingCoach] = useState(true)

  // Extract scheduling conditions from coachDetails
  const schedulingConditions = React.useMemo(
    () => coachDetails?.schedulingConditions || [],
    [coachDetails],
  )

  // Extract total sessions from coachDetails
  const totalSessions = React.useMemo(() => {
    if (coachDetails?.totalSessions) {
      return coachDetails.totalSessions
    }
    // Fallback: count unique sessionIndices from schedulingConditions
    if (schedulingConditions && schedulingConditions.length > 0) {
      const indices = schedulingConditions
        .map((cond) => cond.sessionIndex)
        .filter((index) => index != null)
      return Math.max(...indices, 0)
    }
    return 0
  }, [coachDetails, schedulingConditions])

  // Helper function to get date from scheduling condition for a session
  // Converts UTC primary date to user's selected timezone
  const getDateFromCondition = React.useCallback(
    (sessionIndex) => {
      const condition = schedulingConditions.find(
        (cond) => cond.sessionIndex === sessionIndex,
      )

      if (!condition) {
        return null
      }

      if (condition.primaryDate) {
        // Convert UTC date to user's selected timezone
        const utcMoment = moment.utc(condition.primaryDate)
        if (clientTimezone && utcMoment.isValid()) {
          return utcMoment.tz(clientTimezone)
        }
        return utcMoment
      }

      return null
    },
    [schedulingConditions, clientTimezone],
  )

  // Helper function to get initial month/year for a session
  // Uses timezone-converted date from condition
  const getInitialMonthForSession = React.useCallback(
    (sessionIndex) => {
      const condition = schedulingConditions.find(
        (cond) => cond.sessionIndex === sessionIndex,
      )

      // If condition is 'on_or_before', use current month instead of primaryDate month
      if (condition?.condition === 'on_or_before') {
        return moment()
          .tz(clientTimezone || 'UTC')
          .startOf('month')
      }

      const conditionDate = getDateFromCondition(sessionIndex)
      if (conditionDate && conditionDate.isValid()) {
        const conditionMonth = conditionDate.clone().startOf('month')
        // If condition is 'on_or_after' and primaryDate month has already passed, use current month
        if (condition?.condition === 'on_or_after') {
          const currentMonthStart = moment()
            .tz(clientTimezone || 'UTC')
            .startOf('month')
          if (conditionMonth.isBefore(currentMonthStart, 'month')) {
            return currentMonthStart
          }
        }
        return conditionMonth
      }
      return moment()
        .tz(clientTimezone || 'UTC')
        .startOf('month')
    },
    [getDateFromCondition, schedulingConditions, clientTimezone],
  )

  // Helper function to get initial date for a session
  // Converts UTC primary date to user's selected timezone
  // Note: This will be overridden by first slot's date if available
  const getInitialDateForSession = React.useCallback(
    (sessionIndex) => {
      const condition = schedulingConditions.find(
        (cond) => cond.sessionIndex === sessionIndex,
      )

      if (!condition) {
        return moment()
          .tz(clientTimezone || 'UTC')
          .format('YYYY-MM-DD')
      }

      if (condition.primaryDate) {
        // Convert UTC date to user's selected timezone
        const primaryMoment = moment.utc(condition.primaryDate)
        if (primaryMoment.isValid()) {
          if (clientTimezone) {
            return primaryMoment.tz(clientTimezone).format('YYYY-MM-DD')
          }
          return primaryMoment.format('YYYY-MM-DD')
        }
      }

      return moment()
        .tz(clientTimezone || 'UTC')
        .format('YYYY-MM-DD')
    },
    [schedulingConditions, clientTimezone],
  )

  // Generate session indices from schedulingConditions (only show sessions that need to be scheduled)
  // If schedulingConditions exist, use their sessionIndex values; otherwise fallback to totalSessions
  // sessionIndex 1 = Pre-Session, sessionIndex 2 = Post-Session, sessionIndex 3+ = Session N
  const sessionIndices = React.useMemo(() => {
    // If we have schedulingConditions, use only those session indices
    if (schedulingConditions && schedulingConditions.length > 0) {
      const indices = schedulingConditions
        .map((cond) => cond.sessionIndex)
        .filter((index) => index != null)
        .sort((a, b) => a - b) // Sort ascending
      return [...new Set(indices)] // Remove duplicates
    }
    // Fallback: generate from totalSessions if no schedulingConditions
    if (totalSessions <= 0) {
      return []
    }
    return Array.from({ length: totalSessions }, (_, i) => i + 1)
  }, [schedulingConditions, totalSessions])

  // Extract coach information
  const coachId = coachDetails?.coachId
  const scheduleConfig = coachDetails?.scheduleConfig
  const firstSlotDuration = scheduleConfig?.firstSlotDuration
  const otherSlotsDuration = scheduleConfig?.otherSlotsDuration
  const sowSessionDuration =
    otherSlotsDuration || coachDetails?.allSessionDuration
  const sowId = coachDetails?.sowId || coachDetails?.sow?._id
  const sowType = coachDetails?.sowType || coachDetails?.sow?.sowType

  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [timeSlots, setTimeSlots] = useState([])
  const [availableDays, setAvailableDays] = useState({})
  const [currentMonth, setCurrentMonth] = useState(() =>
    moment().startOf('month'),
  )
  const [isLoadingSlots, setIsLoadingSlots] = useState(false)
  const preferredDateRef = useRef(selectedDate)
  const [timeFormat, setTimeFormat] = useState('12h')
  const [isScheduling, setIsScheduling] = useState(false)
  const lastLoadedSessionMonthRef = useRef(null) // Track last loaded sessionIndex + month to prevent duplicate calls
  const lastLoadedSessionIndexRef = useRef(null) // Track last loaded sessionIndex to detect session switches

  const timezoneOffsetMinutes = useMemo(() => {
    if (!clientTimezone) {
      return null
    }
    return getTimezoneOffsetMinutes(clientTimezone)
  }, [clientTimezone])

  const coachName = useMemo(() => {
    const name = coachDetails?.coach?.fullName
    return name && String(name).trim() ? String(name).trim() : '-'
  }, [coachDetails])

  // Fetch coach details from getScheduling API only if not provided as prop
  useEffect(() => {
    if (coachDetailsProp) {
      setCoachDetails(coachDetailsProp)
      setIsLoadingCoach(false)
      return
    }

    const fetchCoachDetails = async () => {
      if (!userId || !finalQuestId) {
        setIsLoadingCoach(false)
        return
      }

      try {
        setIsLoadingCoach(true)
        const { data } = await API.session.getScheduling(userId, finalQuestId)

        if (data?.success) {
          const toBeScheduled = data?.toBeScheduled || []
          const scheduled = data?.scheduled || []

          const coachData =
            toBeScheduled.length > 0
              ? toBeScheduled[0]
              : scheduled.length > 0
              ? scheduled[0]
              : null

          if (coachData) {
            setCoachDetails(coachData)
          }
        }
        setIsLoadingCoach(false)
      } catch (error) {
        Error.showError(error)
        setIsLoadingCoach(false)
      }
    }

    fetchCoachDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, finalQuestId, coachDetailsProp])

  // Use shared date conversion utility
  const convertDateAndSlot = useCallback(
    (selectedDateStr, slotStartUTC, slotEndUTC = null) => {
      return convertDateAndSlotToUTC(
        selectedDateStr,
        slotStartUTC,
        slotEndUTC,
        clientTimezone,
      )
    },
    [clientTimezone],
  )

  const loadMonthSlots = useCallback(
    async (targetMonth, preferredDate, sessionIndex) => {
      if (!coachId || !targetMonth || !sessionIndex) {
        return
      }

      try {
        setIsLoadingSlots(true)
        const normalizedMonth = moment(targetMonth).startOf('month')

        if (!normalizedMonth.isValid()) {
          setIsLoadingSlots(false)
          return
        }

        const monthNumber = normalizedMonth.month() + 1
        const yearNumber = normalizedMonth.year()

        // Check if we've already loaded this combination to prevent duplicate calls
        // But always call if sessionIndex changed (to support switching back to previously viewed sessions)
        const loadKey = `${sessionIndex}-${yearNumber}-${monthNumber}`
        const sessionChanged =
          lastLoadedSessionIndexRef.current !== sessionIndex

        if (!sessionChanged && lastLoadedSessionMonthRef.current === loadKey) {
          setIsLoadingSlots(false)
          return
        }

        const payload = {
          month: Number(monthNumber),
          year: Number(yearNumber),
          sessionIndex: Number(sessionIndex),
        }

        if (sowSessionDuration) {
          payload.sowSessionDuration = sowSessionDuration
        }
        if (sowId) {
          payload.sowId = sowId
        }
        if (clientTimezone) {
          payload.clientTimezone = clientTimezone
        }

        const { data } = await API.session.getAvailableSlots(coachId, payload)

        // Mark this combination as loaded
        lastLoadedSessionMonthRef.current = loadKey
        lastLoadedSessionIndexRef.current = sessionIndex

        const daysMap = (data?.days || []).reduce((acc, day) => {
          if (day?.date) {
            acc[day.date] = day
          }
          return acc
        }, {})

        // Replace availableDays for this month (don't merge to avoid stale data)
        setAvailableDays((prev) => {
          // Remove old data for this month to avoid confusion
          const filtered = Object.keys(prev).reduce((acc, date) => {
            const dateMoment = moment(date)
            if (
              dateMoment.month() + 1 === monthNumber &&
              dateMoment.year() === yearNumber
            ) {
              // Don't include old data for this month
              return acc
            }
            acc[date] = prev[date]
            return acc
          }, {})
          // Add new data
          return { ...filtered, ...daysMap }
        })

        const nextSelectableDate = (() => {
          // If preferredDate exists and has slots, use it (for revisiting sessions with saved slots)
          if (preferredDate && daysMap[preferredDate]?.slots?.length) {
            return preferredDate
          }

          // Otherwise, use first slot's date (for initial load when no date is selected)
          const firstDayWithSlots = Object.values(daysMap).find(
            (day) => day?.slots?.length,
          )

          if (firstDayWithSlots?.date) {
            return firstDayWithSlots.date
          }

          return null
        })()

        // Only update selectedDate if we have a valid date and it's different
        if (nextSelectableDate && nextSelectableDate !== selectedDate) {
          setSelectedDate(nextSelectableDate)
        }
        // Don't clear selectedSlot here - let the timeSlots useEffect handle it
      } catch (error) {
        Error.showError(error)
        setAvailableDays({})
        setSelectedDate(null)
        setSelectedSlot(null)
      } finally {
        setIsLoadingSlots(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coachId, sowSessionDuration, clientTimezone, sowId, schedulingConditions],
  )

  useEffect(() => {
    preferredDateRef.current = selectedDate
  }, [selectedDate])

  // Single useEffect to handle month changes only (session changes handled in handleSessionClick)
  useEffect(() => {
    if (
      !coachId ||
      !currentMonth ||
      isLoadingCoach ||
      currentSessionIndex === null
    ) {
      return
    }

    // Check cache to prevent duplicate calls
    const monthNumber = currentMonth.month() + 1
    const yearNumber = currentMonth.year()
    const loadKey = `${currentSessionIndex}-${yearNumber}-${monthNumber}`

    // Only load if we haven't already loaded this exact combination
    if (lastLoadedSessionMonthRef.current === loadKey) {
      return
    }

    loadMonthSlots(currentMonth, preferredDateRef.current, currentSessionIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId, currentMonth, loadMonthSlots, isLoadingCoach])

  useEffect(() => {
    // Auto-select first available date if no date is selected or selected date has no slots
    // But don't override if there's a saved slot for this session (to preserve user's selection when revisiting)
    if (
      currentSessionIndex !== null &&
      (!selectedDate || !availableDays[selectedDate]?.slots?.length) &&
      Object.keys(availableDays).length > 0 &&
      currentMonth
    ) {
      // Check if there's a saved slot for this session
      const savedSlot = sessionSlots[currentSessionIndex]
      if (savedSlot?.date && availableDays[savedSlot.date]?.slots?.length) {
        // Use saved date if it has slots available
        setSelectedDate(savedSlot.date)
        return
      }

      // Otherwise, use first available date in current month
      const firstDayWithSlots = Object.values(availableDays).find((day) => {
        if (!day?.slots?.length || !day?.date) return false
        // Only consider dates in the current month
        const dateMoment = moment(day.date)
        return (
          dateMoment.month() === currentMonth.month() &&
          dateMoment.year() === currentMonth.year()
        )
      })
      if (firstDayWithSlots?.date) {
        setSelectedDate(firstDayWithSlots.date)
        return
      }
    }

    // Check if selectedDate is in the current month being displayed
    if (selectedDate && currentMonth) {
      const selectedDateMoment = moment(selectedDate)
      const isInCurrentMonth =
        selectedDateMoment.month() === currentMonth.month() &&
        selectedDateMoment.year() === currentMonth.year()

      // If selectedDate is not in the current month, clear time slots
      if (!isInCurrentMonth) {
        setTimeSlots([])
        setSelectedSlot(null)
        return
      }
    }

    if (selectedDate && availableDays[selectedDate]?.slots?.length) {
      const slots = availableDays[selectedDate].slots
      setTimeSlots(slots)

      // If we have a saved slot for this session and date, try to restore it
      const savedSlot = sessionSlots[currentSessionIndex]
      if (savedSlot && savedSlot.date === selectedDate && savedSlot.slotStart) {
        // Find the matching slot from the available slots by comparing start time
        // Try exact match first
        let matchingSlot = slots.find(
          (slot) => slot.start === savedSlot.slotStart,
        )

        // If no exact match, try comparing ISO strings (handles different formats)
        if (!matchingSlot) {
          matchingSlot = slots.find((slot) => {
            try {
              const slotStartISO = moment.utc(slot.start).toISOString()
              const savedStartISO = moment
                .utc(savedSlot.slotStart)
                .toISOString()
              return slotStartISO === savedStartISO
            } catch (e) {
              return false
            }
          })
        }

        // If still no match, try comparing by moment objects (more flexible)
        if (!matchingSlot) {
          matchingSlot = slots.find((slot) => {
            try {
              const slotMoment = moment.utc(slot.start)
              const savedMoment = moment.utc(savedSlot.slotStart)
              return slotMoment.isSame(savedMoment)
            } catch (e) {
              return false
            }
          })
        }

        if (matchingSlot) {
          setSelectedSlot(matchingSlot)
        } else {
          // If no match found, clear selectedSlot
          setSelectedSlot(null)
        }
      } else if (!savedSlot || savedSlot.date !== selectedDate) {
        // If no saved slot for this date, clear selectedSlot
        setSelectedSlot(null)
      }
      return
    }

    setTimeSlots([])
    // Clear selectedSlot if no slots available for this date
    if (selectedDate && !availableDays[selectedDate]?.slots?.length) {
      setSelectedSlot(null)
    }
  }, [
    selectedDate,
    availableDays,
    currentSessionIndex,
    sessionSlots,
    currentMonth,
  ])

  // Initialize with first session when coachDetails loads
  useEffect(() => {
    if (
      coachDetails &&
      !isLoadingCoach &&
      sessionIndices.length > 0 &&
      currentSessionIndex === null
    ) {
      const firstSessionIndex = sessionIndices[0]
      setCurrentSessionIndex(firstSessionIndex)

      // Use primary date from condition (converted to user timezone) as initial preference
      // But actual selected date will be set to first slot's date after slots are loaded
      const initialDate = getInitialDateForSession(firstSessionIndex)
      const initialMonth = getInitialMonthForSession(firstSessionIndex)

      // Set initial month, but don't set selectedDate yet - let loadMonthSlots set it to first slot's date
      setCurrentMonth(initialMonth)
      preferredDateRef.current = initialDate // Keep as preference, but first slot will override
      lastLoadedSessionMonthRef.current = null // Reset to allow first load
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coachDetails,
    isLoadingCoach,
    sessionIndices,
    getInitialDateForSession,
    getInitialMonthForSession,
  ])

  // Session switching is now handled in handleSessionClick to avoid multiple useEffects

  const clearSessionError = useCallback((sessionIndex) => {
    if (!sessionIndex) {
      return
    }
    setSessionSlotErrors((prev) => {
      if (!prev[sessionIndex]) {
        return prev
      }
      const next = { ...prev }
      delete next[sessionIndex]
      return next
    })
  }, [])

  const handleDateSelect = (date) => {
    setSelectedDate(date)
    setSelectedSlot(null)
    clearSessionError(currentSessionIndex)
  }

  const handleMonthChange = useCallback((month) => {
    if (!month) {
      return
    }

    const normalizedMonth = moment(month).startOf('month')
    if (!normalizedMonth.isValid()) {
      return
    }

    setCurrentMonth(normalizedMonth.clone())
  }, [])

  const handleSlotSelect = (slot) => {
    if (
      selectedSlot &&
      slot &&
      selectedSlot.start === slot.start &&
      selectedSlot.end === slot.end
    ) {
      return
    }

    setSelectedSlot(slot)
    clearSessionError(currentSessionIndex)

    // Immediately save the slot to sessionSlots when selected
    if (selectedDate && slot) {
      const durationMinutes = Number(sowSessionDuration) || 0
      const { start: slotStart, end: slotEndFromConversion } =
        convertDateAndSlot(selectedDate, slot.start, slot.end)

      // Safety check: ensure slotStart is valid before cloning
      if (!slotStart) {
        Error.showError({
          message: 'Unable to process time slot. Please try again.',
        })
        return
      }

      const slotEnd = durationMinutes
        ? slotStart.clone().add(durationMinutes, 'minutes')
        : slotEndFromConversion || slotStart.clone()

      const sessionLabel = getSessionLabel(currentSessionIndex)
      const getTimeslotEventMeta = () => {
        // Quest sessions use Preparation / Integration / Experience labels
        if (sowType === 'QUEST') {
          if (currentSessionIndex === 1) {
            return {
              event: ExperienceEvent.event.selectPreparationSessionTimeslot,
              category:
                ExperienceEvent.category
                  .selectPreparationSessionTimeslotCategory,
              action: 'User select preparation session timeslot',
            }
          }

          if (currentSessionIndex === 2) {
            return {
              event: ExperienceEvent.event.selectIntegrationSessionTimeslot,
              category:
                ExperienceEvent.category
                  .selectIntegrationSessionTimeslotCategory,
              action: 'User select integration session timeslot',
            }
          }

          if (currentSessionIndex === 3) {
            return {
              event: ExperienceEvent.event.selectExperienceSessionTimeslot,
              category:
                ExperienceEvent.category
                  .selectExperienceSessionTimeslotCategory,
              action: 'User select experience session timeslot',
            }
          }
        }

        // Regular sessions use one shared event; sessionLabel identifies Session 1/2/3
        return {
          event: ExperienceEvent.event.selectRegularSessionTimeslot,
          category:
            ExperienceEvent.category.selectRegularSessionTimeslotCategory,
          action: 'User select regular session timeslot',
        }
      }

      const timeslotEventMeta = getTimeslotEventMeta()
      const slotStartDisplay = clientTimezone
        ? moment
            .utc(slot.start)
            .tz(clientTimezone)
            .format(timeFormat === '12h' ? 'h:mm A' : 'HH:mm')
        : timezoneOffsetMinutes !== null
        ? moment
            .utc(slot.start)
            .utcOffset(timezoneOffsetMinutes)
            .format(timeFormat === '12h' ? 'h:mm A' : 'HH:mm')
        : moment
            .utc(slot.start)
            .format(timeFormat === '12h' ? 'h:mm A' : 'HH:mm')

      TagManager.dataLayer({
        dataLayer: {
          event: timeslotEventMeta.event,
          eventProps: {
            category: timeslotEventMeta.category,
            action: timeslotEventMeta.action,
            label: 'Select session timeslot',
            value:
              sowType === 'QUEST' && finalQuestId
                ? `${timeslotEventMeta.action} (experienceId - ${finalQuestId}, sessionLabel - ${sessionLabel}, date - ${selectedDate}, slotStart - ${slotStartDisplay})`
                : `${timeslotEventMeta.action} (sessionLabel - ${sessionLabel}, date - ${selectedDate}, slotStart - ${slotStartDisplay})`,
            userId,
          },
        },
      })

      setSessionSlots((prev) => ({
        ...prev,
        [currentSessionIndex]: {
          date: selectedDate,
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
        },
      }))
    }
  }

  // Custom handleTimezoneChange that extends the hook's function
  const handleTimezoneChangeWithClear = (e) => {
    handleTimezoneChange(e) // Use hook's function
    const newTimezone = e.target.value
    setClientTimezone(newTimezone)

    // Clear all previously scheduled slots - process starts again with new timezone
    setSessionSlots({})

    // Clear cache to force reload when timezone changes
    lastLoadedSessionMonthRef.current = null

    // Reset to first session (preparation session) since process restarts
    // If user was on session 3 or later, move them back to session 1
    if (sessionIndices.length > 0) {
      const firstSessionIndex = sessionIndices[0]

      // Reset to first session
      setCurrentSessionIndex(firstSessionIndex)
      lastLoadedSessionIndexRef.current = firstSessionIndex

      // Reset available days to force reload
      setAvailableDays({})

      // Clear selected slot and time slots since timezone changed
      setSelectedSlot(null)
      setTimeSlots([])

      // Reset to initial date and month for first session (based on new timezone)
      const initialDate = getInitialDateForSession(firstSessionIndex)
      const initialMonth = getInitialMonthForSession(firstSessionIndex)

      setSelectedDate(initialDate)
      preferredDateRef.current = initialDate

      // Update month to trigger reload with new timezone
      setCurrentMonth(initialMonth)
    }
  }

  // Check if a session can be accessed (must book in order, but can go back to update)
  const canAccessSession = (sessionIndex) => {
    if (sessionIndex === sessionIndices[0]) {
      return true
    }

    const currentIndex = sessionIndices.indexOf(sessionIndex)
    if (currentIndex <= 0) {
      return true
    }

    const previousSessionIndex = sessionIndices[currentIndex - 1]
    const previousSessionSlot = sessionSlots[previousSessionIndex]

    // If trying to go forward, previous session must be booked
    if (currentIndex > sessionIndices.indexOf(currentSessionIndex)) {
      return previousSessionSlot?.slotStart !== undefined
    }

    // If going back, always allow (for updating)
    return true
  }

  const applySessionErrors = useCallback(
    (errors) => {
      if (!errors?.length) {
        return
      }

      const errorMap = {}
      errors.forEach(({ sessionIndex, message, code }) => {
        if (sessionIndex != null) {
          errorMap[sessionIndex] = { message, code }
        }
      })
      setSessionSlotErrors(errorMap)

      const firstSessionIndex = errors[0]?.sessionIndex
      if (firstSessionIndex) {
        handleSessionClick(firstSessionIndex)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionSlots, currentSessionIndex, selectedDate, selectedSlot],
  )

  useEffect(() => {
    applySessionErrorsRef.current = applySessionErrors
  }, [applySessionErrors])

  const handleSessionClick = (sessionIndex) => {
    if (sessionIndex === currentSessionIndex) {
      return
    }

    // Check if session can be accessed
    if (!canAccessSession(sessionIndex)) {
      const currentIndex = sessionIndices.indexOf(sessionIndex)
      const previousSessionIndex = sessionIndices[currentIndex - 1]
      Error.showError({
        message: `Please book ${getSessionLabel(
          previousSessionIndex,
        )} first before booking ${getSessionLabel(sessionIndex)}.`,
      })
      return
    }

    // Save current session's selection before switching
    if (selectedDate && selectedSlot) {
      const durationMinutes = Number(sowSessionDuration) || 0
      const { start: slotStart, end: slotEndFromConversion } =
        convertDateAndSlot(selectedDate, selectedSlot.start, selectedSlot.end)

      // Safety check: ensure slotStart is valid before cloning
      if (!slotStart) {
        Error.showError({
          message: 'Unable to process time slot. Please try again.',
        })
        return
      }

      const slotEnd = durationMinutes
        ? slotStart.clone().add(durationMinutes, 'minutes')
        : slotEndFromConversion || slotStart.clone()

      setSessionSlots((prev) => ({
        ...prev,
        [currentSessionIndex]: {
          date: selectedDate,
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
        },
      }))
    }

    // Handle session switch - update state and load data if needed
    const savedSlot = sessionSlots[sessionIndex]
    let targetDate = null
    let targetMonth = null

    if (savedSlot) {
      // Use saved date if available
      const dateMoment = moment(savedSlot.date)
      if (dateMoment.isValid()) {
        targetDate = savedSlot.date
        targetMonth = dateMoment.clone().startOf('month')
      }
    } else {
      // Use initial date from scheduling condition
      targetDate = getInitialDateForSession(sessionIndex)
      targetMonth = getInitialMonthForSession(sessionIndex)
    }

    // Update session index first
    setCurrentSessionIndex(sessionIndex)
    lastLoadedSessionIndexRef.current = sessionIndex

    // Update date and month
    if (targetDate) {
      setSelectedDate(targetDate)
      preferredDateRef.current = targetDate
    }
    if (targetMonth) {
      // Check if we already have cached data for this session/month combination
      const monthNumber = targetMonth.month() + 1
      const yearNumber = targetMonth.year()
      const loadKey = `${sessionIndex}-${yearNumber}-${monthNumber}`

      // Only reset cache if we don't have this data
      if (lastLoadedSessionMonthRef.current !== loadKey) {
        lastLoadedSessionMonthRef.current = null
      }

      // Update month - this will trigger the useEffect to load data if needed
      setCurrentMonth(targetMonth)
    }
  }

  const getSessionLabel = (sessionIndex) => {
    // Check if this is a QUEST type session
    const sowType = coachDetails?.sowType || coachDetails?.sow?.sowType

    if (sowType === 'QUEST') {
      // For QUEST sessions, show Pre-Session, Post-Session based on sessionIndex
      if (sessionIndex === 1) {
        return 'Preparation Session'
      } else if (sessionIndex === 2) {
        return 'Integration Session'
      } else if (sessionIndex === 3) {
        return 'Experience Session'
      }
      return `Session ${sessionIndex}`
    } else {
      // For coaching: show Session 1, Session 2, Session 3
      return `Session ${sessionIndex}`
    }
  }

  const handleNext = () => {
    if (!selectedDate || !selectedSlot) {
      Error.showError({ message: 'Please select a date and time slot' })
      return
    }

    // Save current session's selection
    const durationMinutes = Number(sowSessionDuration) || 0
    const { start: slotStart, end: slotEndFromConversion } =
      convertDateAndSlotToUTC(
        selectedDate,
        selectedSlot.start,
        selectedSlot.end,
        clientTimezone,
      )

    // Safety check: ensure slotStart is valid before cloning
    if (!slotStart) {
      Error.showError({
        message: 'Unable to process time slot. Please try again.',
      })
      return
    }

    const slotEnd = durationMinutes
      ? slotStart.clone().add(durationMinutes, 'minutes')
      : slotEndFromConversion || slotStart.clone()

    const updatedSlots = {
      ...sessionSlots,
      [currentSessionIndex]: {
        date: selectedDate,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd.toISOString(),
      },
    }

    setSessionSlots(updatedSlots)

    // Move to next session in the list
    const currentIndex = sessionIndices.indexOf(currentSessionIndex)
    if (currentIndex < sessionIndices.length - 1) {
      const nextSessionIndex = sessionIndices[currentIndex + 1]

      // Handle session switch - update state and load data if needed (same logic as handleSessionClick)
      const savedSlot = updatedSlots[nextSessionIndex]
      let targetDate = null
      let targetMonth = null

      if (savedSlot) {
        // Use saved date if available
        const dateMoment = moment(savedSlot.date)
        if (dateMoment.isValid()) {
          targetDate = savedSlot.date
          targetMonth = dateMoment.clone().startOf('month')
        }
      } else {
        // Use initial date from scheduling condition
        targetDate = getInitialDateForSession(nextSessionIndex)
        targetMonth = getInitialMonthForSession(nextSessionIndex)
      }

      // Update session index first
      setCurrentSessionIndex(nextSessionIndex)
      lastLoadedSessionIndexRef.current = nextSessionIndex

      // Update date and month
      if (targetDate) {
        setSelectedDate(targetDate)
        preferredDateRef.current = targetDate
      }
      if (targetMonth) {
        // Check if we already have cached data for this session/month combination
        const monthNumber = targetMonth.month() + 1
        const yearNumber = targetMonth.year()
        const loadKey = `${nextSessionIndex}-${yearNumber}-${monthNumber}`

        // Only reset cache if we don't have this data
        if (lastLoadedSessionMonthRef.current !== loadKey) {
          lastLoadedSessionMonthRef.current = null
        }

        // Update month - this will trigger the useEffect to load data if needed
        setCurrentMonth(targetMonth)
      }
    }
  }

  const handleScheduleAll = async () => {
    if (!selectedDate || !selectedSlot) {
      Error.showError({ message: 'Please select a date and time slot' })
      return
    }

    // Save current session's selection
    const durationMinutes = Number(sowSessionDuration) || 0
    const { start: slotStart, end: slotEndFromConversion } =
      convertDateAndSlotToUTC(
        selectedDate,
        selectedSlot.start,
        selectedSlot.end,
        clientTimezone,
      )

    // Safety check: ensure slotStart is valid before cloning
    if (!slotStart) {
      Error.showError({
        message: 'Unable to process time slot. Please try again.',
      })
      return
    }

    const slotEnd = durationMinutes
      ? slotStart.clone().add(durationMinutes, 'minutes')
      : slotEndFromConversion || slotStart.clone()

    const finalSlots = {
      ...sessionSlots,
      [currentSessionIndex]: {
        date: selectedDate,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd.toISOString(),
      },
    }

    // Verify all sessions have slots
    const allSessionsHaveSlots = sessionIndices.every(
      (sessionIndex) => finalSlots[sessionIndex]?.slotStart,
    )

    if (!allSessionsHaveSlots) {
      Error.showError({
        message: 'Please select slots for all sessions before scheduling.',
      })
      return
    }

    const bookedSessionLabels = sessionIndices
      .map((sessionIndex) => getSessionLabel(sessionIndex))
      .join(', ')

    if (sessionIndices.length === 1) {
      gtmScheduleSessionAction(
        bookedSessionLabels,
        userId,
        sowType === 'QUEST' ? finalQuestId : undefined,
      )
    } else {
      gtmScheduleMultipleSessionsAction(
        sessionIndices.length,
        bookedSessionLabels,
        userId,
        sowType === 'QUEST' ? finalQuestId : undefined,
      )
    }

    if (!sowId) {
      Notification.showNotification(
        'Unable to find coaching plan details. Please try again later.',
        'danger',
      )
      return
    }

    try {
      setIsScheduling(true)

      // Prepare payload with sessionIndex for each slot
      const timeSlotsPayload = sessionIndices.map((sessionIndex) => {
        const slotData = finalSlots[sessionIndex]
        const startMoment = moment.utc(slotData.slotStart)
        const endMoment = moment.utc(slotData.slotEnd)

        return {
          date: slotData.date,
          startTime: startMoment.toISOString(),
          endTime: endMoment.toISOString(),
          ...(sowType === 'QUEST' && { sessionIndex: Number(sessionIndex) }),
        }
      })
      const payload = {
        sowId,
        timeSlots: timeSlotsPayload,
      }

      if (clientTimezone) {
        payload.clientTimezone = clientTimezone
      }

      await runScheduling(
        (schedulePayload) => API.session.scheduleAllSessions(schedulePayload),
        payload,
      )
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsScheduling(false)
    }
  }

  const currentIndex = sessionIndices.indexOf(currentSessionIndex)
  const isLastSession = currentIndex === sessionIndices.length - 1
  const isSubmitting = isScheduling || isConfirmingWeekly
  const isActionDisabled = isSubmitting || !selectedDate || !selectedSlot
  const isSchedulingSingleSession = sessionIndices.length === 1
  const schedulePageTitle = isSchedulingSingleSession
    ? 'Schedule Session'
    : 'Schedule Multiple Sessions'
  const scheduleButtonText = isSchedulingSingleSession
    ? 'SCHEDULE'
    : 'Schedule Multiple Sessions'

  if (isLoadingCoach) {
    return (
      <div className='text-center py-5'>
        <div className='spinner-border' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (!coachId) {
    return (
      <div className='text-center py-5'>
        <p>Unable to load coach details. Please try again later.</p>
      </div>
    )
  }

  if (sessionIndices.length === 0) {
    return (
      <div className='text-center py-5'>
        <p>No sessions available to schedule.</p>
      </div>
    )
  }

  if (currentSessionIndex === null) {
    return (
      <div className='text-center py-5'>
        <div className='spinner-border' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='first-session-schedule'>
      <div className='s1'>{schedulePageTitle}</div>
      <Container className='first-session-schedule pb-32px'>
        <CoachDetailsSection
          coachName={coachName}
          duration={sowSessionDuration}
          totalSessions={sessionIndices.length}
          clientTimezone={clientTimezone}
          onTimezoneChange={handleTimezoneChangeWithClear}
          firstSlotDuration={firstSlotDuration}
          otherSlotsDuration={otherSlotsDuration}
          sowType={sowType}
        />

        <Row className='mt-32px first-session-schedule__conditional-planner border border-dark border-1 rounded'>
          <Col
            lg={4}
            md={12}
            className='pl-30px pr-30px border border-dark border-1 border-top-0 border-bottom-0 border-start-0 first-session-schedule__planner-column'>
            <div className='session-index-list mt-3 h-100'>
              <div className='session-index-list__items'>
                {sessionIndices.map((sessionIndex) => {
                  const isActive = sessionIndex === currentSessionIndex
                  const hasSlot = sessionSlots[sessionIndex]?.slotStart
                  const isAccessible = canAccessSession(sessionIndex)
                  const isDisabled = !isAccessible || isSubmitting
                  const sessionError = sessionSlotErrors[sessionIndex]
                  const isRestriction =
                    sessionError && isSessionRestrictionError(sessionError.code)

                  return (
                    <div
                      key={sessionIndex}
                      role='button'
                      tabIndex={isDisabled ? -1 : 0}
                      className={classnames(
                        'session-index-item py-2 w-100 text-center d-flex flex-column align-items-center',
                        {
                          'session-index-item--selected':
                            isActive && !sessionError,
                          'session-index-item--error': sessionError,
                          'session-index-item--restriction':
                            sessionError && isRestriction,
                          'session-index-item--disabled': isDisabled,
                        },
                      )}
                      onClick={() => {
                        if (!isDisabled) {
                          handleSessionClick(sessionIndex)
                        }
                      }}
                      onKeyDown={(event) => {
                        if (
                          !isDisabled &&
                          (event.key === 'Enter' || event.key === ' ')
                        ) {
                          event.preventDefault()
                          handleSessionClick(sessionIndex)
                        }
                      }}>
                      <div className='s8 font-weight-bold'>
                        {getSessionLabel(sessionIndex)}
                      </div>
                      {sessionError && (
                        <div
                          className={classnames(
                            's8 mt-1 px-2 session-index-item__error-message',
                            isRestriction ? 'text-saffron-700' : 'text-danger',
                          )}>
                          {sessionError.message}
                          {isSessionErrorClickable(sessionError.code) && (
                            <span> Click to choose another date or time.</span>
                          )}
                        </div>
                      )}
                      {hasSlot && (
                        <div
                          className={classnames('s8 mt-1', {
                            'text-white-50': isActive && !sessionError,
                            'text-muted': !isActive || sessionError,
                          })}>
                          {moment(sessionSlots[sessionIndex].date).format(
                            'MMM D',
                          )}
                          {', '}
                          {clientTimezone
                            ? moment
                                .utc(sessionSlots[sessionIndex].slotStart)
                                .tz(clientTimezone)
                                .format(
                                  timeFormat === '12h' ? 'h:mm A' : 'HH:mm',
                                )
                            : timezoneOffsetMinutes !== null
                            ? moment
                                .utc(sessionSlots[sessionIndex].slotStart)
                                .utcOffset(timezoneOffsetMinutes)
                                .format(
                                  timeFormat === '12h' ? 'h:mm A' : 'HH:mm',
                                )
                            : moment(
                                sessionSlots[sessionIndex].slotStart,
                              ).format(
                                timeFormat === '12h' ? 'h:mm A' : 'HH:mm',
                              )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </Col>
          <Col
            lg={5}
            md={12}
            className='pl-20px pr-20px border border-dark border-1 border-top-0 border-bottom-0 border-start-0 first-session-schedule__planner-column'>
            <SessionDatePicker
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              minDate={moment().format('YYYY-MM-DD')}
              currentMonth={currentMonth}
              onMonthChange={handleMonthChange}
              availableDays={availableDays}
              disablePastDates={true}
              isLoading={isLoadingSlots}
              disableDatesWithoutSlots={true}
            />
          </Col>
          <Col
            lg={3}
            md={12}
            className='first-session-schedule__planner-column'>
            <TimeSlotPicker
              slots={timeSlots}
              selectedSlot={selectedSlot}
              onSlotSelect={handleSlotSelect}
              timeFormat={timeFormat}
              onTimeFormatChange={setTimeFormat}
              isLoading={isLoadingSlots}
              timezoneOffsetMinutes={timezoneOffsetMinutes}
              timezone={clientTimezone}
            />
          </Col>
          <Col
            lg={12}
            md={12}
            className='d-flex px-0 justify-content-end mt-32px mb-32px'>
            <div>
              {isLastSession ? (
                <Button
                  variant={isActionDisabled ? 'secondary-light' : 'abroad-blue'}
                  size='md'
                  className='line-height-normal'
                  onClick={handleScheduleAll}
                  disabled={isActionDisabled}>
                  {isSubmitting ? 'SCHEDULING...' : scheduleButtonText}
                </Button>
              ) : (
                <Button
                  variant={isActionDisabled ? 'secondary-light' : 'abroad-blue'}
                  size='md'
                  className='line-height-normal cursor-pointer'
                  onClick={handleNext}
                  disabled={isActionDisabled}>
                  NEXT
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Container>
      <WeeklySessionWarningModal
        isOpen={weeklyModal.isOpen}
        message={weeklyModal.message}
        onConfirm={handleWeeklyConfirm}
        onCancel={closeWeeklyModal}
        loading={isConfirmingWeekly}
      />
    </div>
  )
}

export default QuestSessionSchedulingCalendar
